// ==========================================
// COMISSÃO DO PÓS-VENDA POR INDICAÇÃO (#6236) — regra pura
// ==========================================
// Decidido com o Igor em 20/10/2026: quando a negociação nasce de uma indicação que alguém
// do pós-venda registrou (o pós-venda pediu ao cliente e ele indicou), a comissão normal da
// venda passa a valer 120% e é dividida meio a meio entre quem vendeu e o pós-venda.
//   Ex.: comissão normal de R$ 100,00 → R$ 120,00 → R$ 60,00 para a vendedora e R$ 60,00
//   para o pós-venda.
//
// Decisões do Felipe (20/10/2026):
// - Vale para quem é pago na aba Vendedores da Folha: Comercial Interno e vendedor externo.
//   Parceiro segue a regra própria dele.
// - Os kWh da venda contam inteiros na meta de quem vendeu (o pós-venda não tem meta).
// - A parte do pós-venda aparece na aba Vendedores, numa linha só com essa comissão.
// - Conta no mês da aprovação da análise cadastral, como o resto da Folha.
//
// Sem dependência de banco: testado em __tests__/comissaoIndicacaoPosVenda.test.ts. A leitura
// das indicações fica em comissaoIndicacaoPosVendaDb.ts.
import { ehComercialInterno } from './departamentos.ts';
import type { Profile } from '../../types';

export const INDICACAO_POS_VENDA_FATOR = 1.2;
export const INDICACAO_POS_VENDA_PARTE_VENDEDOR = 0.5;

/** Permissão do programa de indicações que identifica o pós-venda (Dhaiane, João Henrique e
 *  Bruna — os dois últimos estão no departamento Financeiro, por isso não dá para usar o depto). */
export const PERMISSAO_POS_VENDA_INDICACOES = 'Trabalhar indicações';

/** `comissao_vendedores.politica` da linha do pós-venda na Folha. */
export const POLITICA_POS_VENDA_INDICACAO = 'pos_venda_indicacao';

const round2 = (v: number) => Math.round(v * 100) / 100;

/** Comissão normal → 120%, metade para cada lado. As duas partes somam exatamente o total. */
export function dividirComissaoIndicacao(valorNormal: number): { total: number; vendedor: number; posVenda: number } {
    const total = round2(valorNormal * INDICACAO_POS_VENDA_FATOR);
    const vendedor = round2(total * INDICACAO_POS_VENDA_PARTE_VENDEDOR);
    return { total, vendedor, posVenda: round2(total - vendedor) };
}

type PerfilPosVenda = Pick<Profile, 'role' | 'departamento' | 'departamentos'> & { permissions?: string[] | null };

/**
 * Conta como pós-venda quem tem a permissão do programa e NÃO é pago como vendedor (role
 * vendedor/parceiro ou Comercial Interno): quem vende já tem a comissão dela, e a mesma pessoa
 * não pode ter duas linhas na Folha no mesmo mês.
 */
export function ehPosVendaIndicacoes(perfil: PerfilPosVenda | null | undefined): boolean {
    if (!perfil) return false;
    const permissoes = Array.isArray(perfil.permissions) ? perfil.permissions : [];
    if (!permissoes.includes(PERMISSAO_POS_VENDA_INDICACOES)) return false;
    if (perfil.role === 'vendedor' || perfil.role === 'parceiro' || perfil.role === 'vendedor_parceiro') return false;
    if (ehComercialInterno(perfil)) return false;
    return true;
}

/**
 * Quem do pós-venda gerou cada negociação: a indicação válida dela foi registrada por alguém
 * do pós-venda, e esse alguém não é o próprio responsável pela venda.
 */
export function posVendaPorNegociacao(
    registros: Array<{ negociacao_id: number | string; registrado_por: string | null }>,
    perfilPorId: Map<string, PerfilPosVenda>,
    responsavelPorNegociacao: Map<number, string | null>,
): Map<number, string> {
    const out = new Map<number, string>();
    for (const r of registros) {
        const negId = Number(r.negociacao_id);
        if (!r.registrado_por || out.has(negId)) continue;
        if (!ehPosVendaIndicacoes(perfilPorId.get(r.registrado_por))) continue;
        if (responsavelPorNegociacao.get(negId) === r.registrado_por) continue;
        out.set(negId, r.registrado_por);
    }
    return out;
}

/** O que sai de uma venda indicada pelo pós-venda — vai para detalhe_calculo e para a linha do pós-venda. */
export interface RepassePosVenda {
    negociacao_id: number;
    pos_venda_id: string;
    kwh: number;
    /** Comissão normal da negociação (100%). */
    valor_normal: number;
    /** 120% da normal. */
    valor_total: number;
    parte_vendedor: number;
    parte_pos_venda: number;
}

export function montarRepasse(negociacaoId: number, posVendaId: string, kwh: number, valorNormal: number): RepassePosVenda {
    const d = dividirComissaoIndicacao(valorNormal);
    return {
        negociacao_id: negociacaoId,
        pos_venda_id: posVendaId,
        kwh,
        valor_normal: round2(valorNormal),
        valor_total: d.total,
        parte_vendedor: d.vendedor,
        parte_pos_venda: d.posVenda,
    };
}

/** Soma por pessoa do pós-venda — a linha dela na Folha. */
export function agruparRepassesPorPosVenda(repasses: RepassePosVenda[]): Map<string, { total: number; kwh: number; itens: RepassePosVenda[] }> {
    const out = new Map<string, { total: number; kwh: number; itens: RepassePosVenda[] }>();
    for (const r of repasses) {
        const atual = out.get(r.pos_venda_id) || { total: 0, kwh: 0, itens: [] };
        atual.total = round2(atual.total + r.parte_pos_venda);
        atual.kwh += r.kwh;
        atual.itens.push(r);
        out.set(r.pos_venda_id, atual);
    }
    return out;
}
